import { APP_LABEL, CLIENT_APP } from "@/constants/extras";
import { GluestackUIProvider } from "@/gluestack-ui-provider";
import { ApolloProvider } from "@apollo/client/react";
import { setupCssInterop } from "@repo/components/src/cssInterop";
import DrawerProvider from "@repo/components/src/globalProviders/drawer";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import "@repo/components/src/i18n";
import {
  calendarFormat,
  calendarPlugin,
  duration,
  graphqlDateFormat,
  localizedFormat,
  localizedShortDate,
  minMax,
  relativeTime,
} from "@repo/components/src/utils/dayjs";
import { client, hasSession, isLoggedIn } from "@repo/graphql/client";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { DefaultTheme, Slot, Theme, ThemeProvider } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useCallback, useEffect, useMemo } from "react";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import "react-native-reanimated";
import {
  SafeAreaProvider,
  initialWindowMetrics,
} from "react-native-safe-area-context";
import colors from "tailwindcss/colors";
import { setLocale } from "yup";
import "../global.css";

dayjs.extend(relativeTime);
dayjs.extend(calendarPlugin);
dayjs.extend(calendarFormat);
dayjs.extend(graphqlDateFormat);
dayjs.extend(localizedFormat);
dayjs.extend(localizedShortDate);
dayjs.extend(duration);
dayjs.extend(minMax);
dayjs.locale("es");

setupCssInterop();

setLocale({
  mixed: {
    oneOf: "Seleccione una opción válida",
  },
});

const navigationThemes = {
  light: {
    primary: colors.neutral[900],
    background: colors.neutral[100],
    card: colors.neutral[100],
    text: colors.neutral[800],
    border: colors.neutral[500],
    notification: colors.neutral[700],
  },
  dark: {
    primary: colors.neutral[100],
    background: colors.neutral[800],
    card: colors.neutral[950],
    text: colors.neutral[300],
    border: colors.neutral[500],
    notification: colors.neutral[700],
  },
};

export default function RootLayout() {
  const THIS_CLIENT = useMemo(() => client(APP_LABEL, CLIENT_APP), []);
  const populateLoggedIn = useCallback(async () => {
    isLoggedIn(await hasSession());
  }, []);
  const mode: "light" | "dark" = "light";
  const navTheme: Theme = useMemo(() => {
    return {
      dark: (mode as "light" | "dark") === "dark",
      colors: navigationThemes[mode],
      fonts: DefaultTheme.fonts,
    };
  }, [mode]);
  useEffect(() => {
    populateLoggedIn();
  }, [populateLoggedIn]);
  return (
    <ApolloProvider client={THIS_CLIENT}>
      <GestureHandlerRootView>
        <SafeAreaProvider initialMetrics={initialWindowMetrics}>
          <GluestackUIProvider mode={mode}>
            <ThemeProvider value={navTheme}>
              <Slot />
              <StatusBar style="auto" />
              <ModalProvider />
              <DrawerProvider />
              <MontuToastProvider />
            </ThemeProvider>
          </GluestackUIProvider>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </ApolloProvider>
  );
}
